import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ExternalLink, Pause, Play, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  SortableTh,
  TablePagination,
  useColumnResize,
  usePagination,
  useTableSort,
} from '@/components/ui/data-table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { setAdStatus } from '@/services/googleAdsManagementService';
import type { MonitoredAd } from '@/services/googleAdsManagementService';
import { fuzzyFilter } from '@/lib/fuzzyMatch';

/**
 * Ad health, as an operator table rather than a dump.
 *
 * ⛔ WHY THE AD COLUMN EXISTS. The first version showed campaign / ad group /
 * approval / cost and nothing identifying the AD. Six different ads in one ad
 * group, all APPROVED_LIMITED at $0.00, rendered as six identical rows and read
 * as a duplication bug. They were 55 distinct ads — verified against the live
 * account. The table was not repeating itself; it was withholding the one field
 * that made the rows different.
 */

const COLUMN_DEFAULTS: Record<string, number> = {
  ad: 220,
  campaign: 200,
  adGroup: 170,
  approval: 150,
  strength: 110,
  impressions: 100,
  cost: 100,
  conversions: 90,
  cost_per_conversion: 110,
  note: 240,
};

/**
 * Ad Strength, colour-coded.
 *
 * Google's scale is POOR → AVERAGE → GOOD → EXCELLENT, with PENDING and
 * UNSPECIFIED while it is still being assessed. POOR is the one that costs
 * impressions, so it reads as an error rather than a neutral label.
 *
 * ⚠ Variants, not colour classes. `success`/`warning`/`destructive` come from
 * the theme tokens, so this stays correct in dark mode and if the palette
 * changes. Hardcoding a green would break both.
 */
function strengthVariant(
  strength: string,
): 'success' | 'warning' | 'destructive' | 'secondary' | 'outline' {
  switch ((strength || '').toUpperCase()) {
    case 'EXCELLENT':
      return 'success';
    case 'GOOD':
      return 'success';
    case 'AVERAGE':
      return 'warning';
    case 'POOR':
      return 'destructive';
    case 'PENDING':
      return 'secondary';
    default:
      // UNSPECIFIED / UNKNOWN / blank — do NOT colour it. A grey "unknown" is
      // honest; painting it green would claim an assessment Google has not made.
      return 'outline';
  }
}

function adLabel(ad: MonitoredAd): string {
  // ⛔ NEVER SHOW AN AD ID AS THE IDENTITY. Viktor, on the first version:
  // "Replace `Ad 806898081504` with the headline; ids are not how anyone
  // recognises an ad." The backend resolves this now (headline → ad name →
  // type + ad group); the fallbacks here only cover an older payload.
  return ad.label?.trim() || ad.headline?.trim() || ad.ad_name?.trim() || `Ad ${ad.ad_id}`;
}

/** Money that bought nothing reads red; everything else stays quiet.
 *  CORE-UI-01: `--destructive` is a dark red on the dark theme, so every
 *  red text/tint here carries a dark: override (TargetChip pattern). */
function costTone(ad: MonitoredAd): string {
  return ad.is_waste ? 'text-destructive font-medium dark:text-red-200' : '';
}

function fmtMoney(n: number): string {
  return `$${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtConversions(n: number): string {
  // ⚠ Google returns fractional conversions (611.444885). Printing the raw
  // float makes the column look broken and invites "why is it 0.6 of a call?".
  return (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function googleAdsUrl(ad: MonitoredAd, customerId: string): string {
  return `https://ads.google.com/aw/ads?campaignId=${ad.campaign_id}&adGroupIds=${ad.ad_group_id}&__c=${customerId}`;
}

export function AdHealthTable({
  ads,
  customerId,
  tenantId,
  focus,
  onClearFocus,
  canSetStatus = false,
}: {
  ads: MonitoredAd[];
  customerId: string;
  tenantId: string;
  /** Set by clicking a summary tile: show only that tile's population. */
  focus?: 'waste' | 'disapproved' | null;
  onClearFocus?: () => void;
  /** ⛔ Whether the SERVER will accept a pause/enable, straight from the read
   *  response — not a local guess, and not a flag the frontend keeps its own
   *  copy of. Defaults false so a payload without it hides the controls: a
   *  button that stops a live ad is the wrong thing to offer optimistically. */
  canSetStatus?: boolean;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  // Shown when a search change dropped a selection, until the next selection.
  const [selectionCleared, setSelectionCleared] = useState(false);
  // Adjusted during render (not in an effect): a new selection hides the note.
  if (selectionCleared && selected.size > 0) setSelectionCleared(false);
  /** Which bulk action is awaiting confirmation, if any. */
  const [pending, setPending] = useState<'PAUSED' | 'ENABLED' | null>(null);
  /** ⛔ WHICH ADS THE CONFIRM DIALOG IS ABOUT — explicit, never inferred from
      the selection. Single-row pause used to call mutate() directly while only
      the BULK path confirmed. That asymmetry was harmless while writes were
      off; the day GOOGLE_ADS_AD_STATUS_ENABLED went true in production it
      became "one tap on a 14px ghost icon stops a live ad, no questions". Both
      paths now open the same diff. null = the current selection (bulk). */
  const [pendingNames, setPendingNames] = useState<string[] | null>(null);

  /** Open the confirm dialog for exactly these ads. */
  const askConfirm = (status: 'PAUSED' | 'ENABLED', names?: string[]) => {
    setPendingNames(names ?? null);
    setPending(status);
  };

  const statusMutation = useMutation({
    mutationFn: ({
      names,
      status,
      expectedCurrent,
    }: {
      names: string[];
      status: 'ENABLED' | 'PAUSED';
      expectedCurrent?: 'ENABLED' | 'PAUSED';
    }) => setAdStatus(tenantId, names, status, expectedCurrent),
    onSuccess: (res, vars) => {
      // ⚠ Report `changed`, never `requested`. Saying "3 ads paused" when one
      // failed tells an operator an ad has stopped while it is still serving
      // and still spending.
      //
      // ⛔ UNDO REVERSES `changed_resource_names`, NOT THE SELECTION. The
      // selection includes ads that failed, and ads the safety cap skipped.
      // Re-enabling those would start ads the operator never paused — and if
      // one of them was paused last month for a reason, nothing would say so.
      const undoable = res.changed_resource_names ?? [];
      const back = vars.status === 'PAUSED' ? 'ENABLED' : 'PAUSED';
      const undoAction =
        undoable.length > 0
          ? {
              label: 'Undo',
              onClick: () =>
                // The undo expects the ads to still be where our write left
                // them. If someone moved one in the Google Ads UI meanwhile,
                // the server refuses that ad rather than reverting them.
                statusMutation.mutate({
                  names: undoable,
                  status: back,
                  expectedCurrent: vars.status,
                }),
            }
          : undefined;

      if (res.drifted?.length) {
        // Not a failure — a refusal, and the operator needs to know which.
        toast.warning(res.message, {
          description: `${res.drifted.length} ad(s) were changed elsewhere and were left alone.`,
          duration: 20000,
        });
        setSelected(new Set());
        queryClient.invalidateQueries({ queryKey: ['ga-ad-monitor'] });
        return;
      }
      if (res.failed.length) {
        toast.warning(res.message, {
          description: `${res.failed.length} did not change.`,
          action: undoAction,
          duration: 15000,
        });
      } else {
        toast.success(res.message, {
          action: undoAction,
          // ⚠ Longer than the default. An undo the operator cannot reach in
          // time is not an undo.
          duration: 15000,
        });
      }
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['ga-ad-monitor'] });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const flag =
        typeof detail === 'object' && detail !== null
          ? (detail as { flag?: string }).flag
          : undefined;
      toast.error(
        flag
          ? `This action is switched off (${flag}).`
          : 'Could not change ad status.',
      );
    },
  });

  const toggleRow = (rn: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rn)) next.delete(rn);
      else next.add(rn);
      return next;
    });
  const [approval, setApproval] = useState('all');
  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<MonitoredAd>('cost', 'desc');
  const { widths, startResize } = useColumnResize(COLUMN_DEFAULTS);

  const approvalOptions = useMemo(() => {
    const seen = new Set(ads.map((a) => a.approval_status).filter(Boolean));
    return Array.from(seen).sort();
  }, [ads]);

  const filtered = useMemo(() => {
    // ⛔ STRICT search (lib/fuzzyMatch): exact / prefix / word / substring
    // only, each field on its own. The selection here feeds bulk Pause /
    // Enable on live ads, so nothing loosely similar may land under it. The
    // table is sorted by the user's column below, so no ranking here.
    const searched = fuzzyFilter(
      ads,
      query,
      (a) => [adLabel(a), a.campaign_name, a.ad_group_name, a.approval_status, a.concern],
      { sort: false, strict: true },
    );
    return searched.filter((a) => {
      // ⛔ THE TILE AND THE TABLE MUST AGREE. A tile that says "3" and then
      // scrolls to a table showing 55 rows teaches the operator the number was
      // decorative. Focus narrows to exactly the population the tile counted.
      if (focus === 'waste' && !a.is_waste) return false;
      if (focus === 'disapproved' && a.approval_status !== 'DISAPPROVED') {
        return false;
      }
      if (approval !== 'all' && a.approval_status !== approval) return false;
      return true;
    });
  }, [ads, query, approval, focus]);

  const sorted = useMemo(
    () =>
      sortRows(filtered, {
        ad: (a) => adLabel(a).toLowerCase(),
        campaign: (a) => a.campaign_name.toLowerCase(),
        adGroup: (a) => a.ad_group_name.toLowerCase(),
        approval: (a) => a.approval_status,
        strength: (a) => a.ad_strength,
        impressions: (a) => a.impressions,
        cost: (a) => a.cost,
        conversions: (a) => a.conversions,
        cost_per_conversion: (a) => a.cost_per_conversion,
        note: (a) => a.concern,
      }),
    [filtered, sortRows],
  );

  const { page, pageSize, setPage, setPageSize, pageCount, slice } =
    usePagination(sorted.length, { defaultPageSize: 20 });
  const rows = slice(sorted);

  const th = (key: string, label: string, className?: string) => (
    <SortableTh
      sortKey={key}
      currentKey={sortKey}
      dir={sortDir}
      onSort={toggleSort}
      resizable
      width={widths[key]}
      onResize={startResize}
      className={className}
    >
      {label}
    </SortableTh>
  );

  // The rows the pending action would actually hit, resolved from the same
  // list the operator is looking at.
  const pendingTargets = pendingNames ?? [...selected];
  const pendingRows = ads.filter((a) => pendingTargets.includes(a.resource_name));
  const pendingSpend = pendingRows.reduce((sum, a) => sum + (a.cost ?? 0), 0);
  const pendingConversions = pendingRows.reduce(
    (sum, a) => sum + (a.conversions ?? 0),
    0,
  );

  return (
    <div className="flex flex-col gap-3">
      {focus && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-coral/40 bg-coral-light/20 p-2 text-xs">
          <span>
            Showing only{' '}
            <strong>
              {focus === 'waste'
                ? 'ads that spent and converted nothing'
                : 'disapproved ads'}
            </strong>{' '}
            ({filtered.length} of {ads.length}).
          </span>
          <Button size="sm" variant="ghost" onClick={onClearFocus}>
            Show all ads
          </Button>
        </div>
      )}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
            setPendingNames(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending === 'PAUSED' ? 'Pause' : 'Enable'} {pendingRows.length}{' '}
              ad{pendingRows.length === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending === 'PAUSED' ? (
                    <>
                      These ads stop serving immediately. Over the last 30 days
                      they spent{' '}
                      <strong>
                        {fmtMoney(pendingSpend)}
                      </strong>{' '}
                      and recorded{' '}
                      <strong>{fmtConversions(pendingConversions)}</strong>{' '}
                      conversions.
                    </>
              ) : (
                <>These ads start serving and spending again.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* ⚠ Name every row. A count alone is what lets someone confirm 40
              ads believing they picked 4. This sits outside the description
              because that renders a <p>, which cannot contain a table. */}
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 font-medium">Ad</th>
                  <th className="p-2 font-medium">Campaign</th>
                  <th className="p-2 text-right font-medium">Cost</th>
                  <th className="p-2 text-right font-medium">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {pendingRows.map((a) => (
                  <tr key={a.resource_name} className="border-t">
                    <td className="p-2">{adLabel(a)}</td>
                    <td className="p-2 text-muted-foreground">
                      {a.campaign_name}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {fmtMoney(a.cost)}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {fmtConversions(a.conversions)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending) {
                  // ⚠ Acts on exactly what the dialog LISTED. Reading `selected`
                  // here would pause a different set from the one just shown if
                  // the selection changed while the dialog was open.
                  statusMutation.mutate({
                    names: pendingTargets,
                    status: pending,
                  });
                }
                setPending(null);
                setPendingNames(null);
              }}
            >
              {pending === 'PAUSED' ? 'Pause' : 'Enable'} {pendingRows.length}{' '}
              ad{pendingRows.length === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {canSetStatus && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-coral/40 bg-coral-light/20 p-2">
          <span className="text-sm font-medium">{selected.size} selected</span>
          {/* ⛔ NEVER MUTATE STRAIGHT FROM THE BAR. These buttons stop and start
              live ads on a real account; a mis-click used to take effect with no
              confirmation and nothing naming what it hit. Viktor: "bulk pause
              with no diff is the one that will burn someone." Both actions now
              open a dialog listing every ad by name, with its 30-day spend and
              conversions, so the operator confirms against what they can see. */}
          <Button
            size="sm"
            variant="outline"
            disabled={statusMutation.isPending}
            onClick={() => askConfirm('PAUSED')}
          >
            <Pause className="mr-1 h-3.5 w-3.5" /> Pause
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={statusMutation.isPending}
            onClick={() => askConfirm('ENABLED')}
          >
            <Play className="mr-1 h-3.5 w-3.5" /> Enable
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // A new search must not leave ads it now hides selected under
              // bulk Pause / Enable, so the selection goes, with a note.
              if (selected.size > 0) {
                setSelected(new Set());
                setSelectionCleared(true);
              }
            }}
            placeholder="Search ads, ad groups, campaigns…"
            className="h-11 md:h-9 pl-8"
          />
        </div>
        <Select value={approval} onValueChange={setApproval}>
          <SelectTrigger className="h-11 w-full md:h-9 md:w-[200px]">
            <SelectValue placeholder="All approval states" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All approval states</SelectItem>
            {approvalOptions.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground tabular-nums">
          {filtered.length} of {ads.length}
        </span>
      </div>

      {selectionCleared && selected.size === 0 && (
        <p className="text-xs text-muted-foreground" role="status">
          Selection cleared because the search changed.
        </p>
      )}

      {/* Desktop table; the phone layout is the card list below. Both read the
          same filtered+sorted rows, so they cannot disagree. */}
      <div className="hidden overflow-x-auto md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {/* Selection exists ONLY to feed the bulk pause/enable bar. With
                  writes off there is nothing to do with a selection, so the
                  column goes too rather than leaving checkboxes that tick and
                  achieve nothing. */}
              {canSetStatus && (
              <TableHead className="w-[40px]">
                <Checkbox
                  aria-label="Select all on this page"
                  checked={rows.length > 0 && rows.every((r) => selected.has(r.resource_name))}
                  onCheckedChange={(v) =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      rows.forEach((r) =>
                        v ? next.add(r.resource_name) : next.delete(r.resource_name),
                      );
                      return next;
                    })
                  }
                />
              </TableHead>
              )}
              {th('ad', 'Ad')}
              {th('campaign', 'Campaign')}
              {th('adGroup', 'Ad group')}
              {th('approval', 'Approval')}
              {th('strength', 'Strength')}
              {th('impressions', 'Impr.', 'text-right')}
              {th('cost', 'Cost', 'text-right')}
              {/* ⛔ THE OUTCOME COLUMNS. Impressions and cost say what was
                  spent; only these two say whether it bought anything. */}
              {th('conversions', 'Conv.', 'text-right')}
              {th('cost_per_conversion', 'Cost/conv.', 'text-right')}
              {th('note', 'Note')}
              <SortableTh
                sortKey="actions"
                currentKey={sortKey}
                dir={sortDir}
                onSort={() => undefined}
                className="w-[90px]"
              >
                Actions
              </SortableTh>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="py-6 text-center text-sm text-muted-foreground">
                  {ads.length === 0
                    ? 'No ads found on this account in the last 30 days.'
                    : 'No ads match this search or filter.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((ad) => (
                <TableRow key={ad.ad_id}>
                  {canSetStatus && (
                    <TableCell>
                      <Checkbox
                        aria-label={`Select ${adLabel(ad)}`}
                        checked={selected.has(ad.resource_name)}
                        onCheckedChange={() => toggleRow(ad.resource_name)}
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-xs font-medium">
                    <div className="truncate" title={adLabel(ad)}>
                      {adLabel(ad)}
                    </div>
                    {ad.final_url ? (
                      <div
                        className="truncate text-[11px] font-normal text-muted-foreground"
                        title={ad.final_url}
                      >
                        {ad.final_url.replace(/^https?:\/\//, '')}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">{ad.campaign_name}</TableCell>
                  <TableCell className="text-xs">{ad.ad_group_name}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        ad.approval_status === 'DISAPPROVED' ? 'destructive' : 'secondary'
                      }
                    >
                      {ad.approval_status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {ad.ad_strength ? (
                      <Badge variant={strengthVariant(ad.ad_strength)}>
                        {ad.ad_strength}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {ad.impressions.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={`text-right text-xs tabular-nums ${costTone(ad)}`}
                  >
                    {fmtMoney(ad.cost)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {fmtConversions(ad.conversions)}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {ad.conversions > 0 && ad.cost_per_conversion > 0 ? (
                      fmtMoney(ad.cost_per_conversion)
                    ) : (
                      // ⚠ An em dash, not "$0.00". Zero cost-per-conversion
                      // would read as "free leads" when it means "no leads".
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {ad.is_waste ? (
                      <span className="text-destructive dark:text-red-200">
                        Spent {fmtMoney(ad.cost)}, no conversions
                      </span>
                    ) : (
                      ad.concern
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {/* Pause and enable are the everyday actions and are
                          reversible, so they sit inline. EDITING an ad is not
                          here on purpose: a replacement destroys the ad's
                          performance history and belongs behind its own confirm
                          flow, never a one-click table icon. */}
                      {/* ⚠ Hidden, not disabled, when the server will refuse the
                          write. A greyed-out pause invites "why can't I click
                          this?"; an absent one asks nothing. The Google Ads link
                          below stays either way — it is view-only, and it is
                          where the operator goes to pause an ad by hand. */}
                      {canSetStatus &&
                        (ad.status === 'ENABLED' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Pause this ad"
                          disabled={statusMutation.isPending}
                          onClick={() => askConfirm('PAUSED', [ad.resource_name])}
                        >
                          <Pause className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Enable this ad"
                          disabled={statusMutation.isPending}
                          onClick={() => askConfirm('ENABLED', [ad.resource_name])}
                        >
                          <Play className="h-3.5 w-3.5" />
                        </Button>
                        ))}
                      <Button asChild size="sm" variant="ghost" title="Open in Google Ads">
                        <a href={googleAdsUrl(ad, customerId)} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* --- phone layout ------------------------------------------------- */}
      <div className="flex flex-col gap-2 md:hidden">
        <div className="flex items-center gap-2">
          <Select value={sortKey ?? 'cost'} onValueChange={(v) => toggleSort(v)}>
            <SelectTrigger className="h-11 flex-1">
              <SelectValue placeholder="Sort by…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cost">Spend</SelectItem>
              <SelectItem value="conversions">Conversions</SelectItem>
              <SelectItem value="cost_per_conversion">Cost/conv.</SelectItem>
              <SelectItem value="impressions">Impressions</SelectItem>
              <SelectItem value="ad">Ad</SelectItem>
              <SelectItem value="campaign">Campaign</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="h-11 shrink-0 px-3"
            onClick={() => sortKey && toggleSort(sortKey)}
          >
            {sortDir === 'desc' ? '↓ High' : '↑ Low'}
          </Button>
        </div>

        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {ads.length === 0
              ? 'No ads found on this account in the last 30 days.'
              : 'No ads match this search or filter.'}
          </p>
        ) : (
          rows.map((ad) => (
            <div
              key={ad.ad_id}
              className={
                'rounded-md border border-border p-3 ' +
                (ad.is_waste ? 'bg-destructive/5 dark:border-red-700/60 dark:bg-red-950/50' : '')
              }
            >
              <div className="flex items-start gap-2">
                {canSetStatus && (
                  <Checkbox
                    aria-label={`Select ${adLabel(ad)}`}
                    className="mt-0.5 h-5 w-5"
                    checked={selected.has(ad.resource_name)}
                    onCheckedChange={() => toggleRow(ad.resource_name)}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="break-words text-sm font-medium">{adLabel(ad)}</div>
                  {ad.final_url ? (
                    <div className="break-all text-[11px] text-muted-foreground">
                      {ad.final_url.replace(/^https?:\/\//, '')}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Badge
                  variant={
                    ad.approval_status === 'DISAPPROVED' ? 'destructive' : 'secondary'
                  }
                >
                  {ad.approval_status || 'unknown'}
                </Badge>
                {ad.ad_strength ? (
                  <Badge variant={strengthVariant(ad.ad_strength)}>
                    {ad.ad_strength}
                  </Badge>
                ) : null}
                <Badge variant="outline">{ad.status}</Badge>
              </div>

              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Campaign</dt>
                <dd className="min-w-0 break-words text-xs">{ad.campaign_name}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Ad group</dt>
                <dd className="min-w-0 break-words text-xs">{ad.ad_group_name}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Spend</dt>
                <dd className={'text-xs tabular-nums ' + costTone(ad)}>{fmtMoney(ad.cost)}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Conv.</dt>
                <dd className="text-xs tabular-nums">{fmtConversions(ad.conversions)}</dd>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Cost/conv.</dt>
                <dd className="text-xs tabular-nums">
                  {ad.conversions > 0 && ad.cost_per_conversion > 0 ? (
                    fmtMoney(ad.cost_per_conversion)
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </dd>
              </dl>

              {/* ⛔ THE PHONE HAD NO WAY OUT. The desktop row carries "Open in
                  Google Ads" and a per-ad pause; the card carried neither, so on
                  a phone the only action was the bulk bar — and the escape hatch
                  the operator doc points at ("use Open in Google Ads on the ad
                  row") did not exist on the layout Jacob actually tests on.
                  Both are 44px targets here, not the 14px ghost icons the
                  desktop row can afford with a mouse. */}
              <div className="mt-2 flex items-center gap-2">
                <Button asChild size="sm" variant="outline" className="h-11 flex-1">
                  <a
                    href={googleAdsUrl(ad, customerId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink className="mr-1.5 h-4 w-4" /> Open in Google Ads
                  </a>
                </Button>
                {canSetStatus &&
                  (ad.status === 'ENABLED' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-11 flex-1"
                      disabled={statusMutation.isPending}
                      onClick={() => askConfirm('PAUSED', [ad.resource_name])}
                    >
                      <Pause className="mr-1.5 h-4 w-4" /> Pause
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-11 flex-1"
                      disabled={statusMutation.isPending}
                      onClick={() => askConfirm('ENABLED', [ad.resource_name])}
                    >
                      <Play className="mr-1.5 h-4 w-4" /> Enable
                    </Button>
                  ))}
              </div>

              {(ad.is_waste || ad.concern) && (
                <p
                  className={
                    'mt-2 text-xs ' +
                    (ad.is_waste ? 'text-destructive dark:text-red-200' : 'text-muted-foreground')
                  }
                >
                  {ad.is_waste
                    ? `Spent ${fmtMoney(ad.cost)}, no conversions`
                    : ad.concern}
                </p>
              )}
            </div>
          ))
        )}
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

export default AdHealthTable;
